import React, { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Input, Label, RadioInput, AbsCheckBox } from "abs-ui-components";
import { MDBCol, MDBRow } from "mdbreact";
import { useSelector } from "react-redux";
import { Controller, useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import {
  INPUT_STEP_VALUE,
  preventScientificNotation,
  roundOff,
} from "../../../services/utilities";
 
const DWTGTChangeDetails = ({
  index,
  isGrossTonnage,
  vesselCapacityChangeDetails,
  isEnrollVessel,
  showWarning,
  reportYear,
}) => {
  const userData = useSelector((state) => state.userInfo?.userData);
  const currentUserEmail =
    userData?.userName ||
    userData?.email ||
    userData?.UserName ||
    userData?.Email ||
    "";
  const userRoles = userData?.roles || userData?.Roles || [];
 
  const canViewReviewWarning = userRoles.some((role) =>
    ["ER_VERIFIER", "ER_REVIEWER", "ER_SUPERADMIN"].includes(
      typeof role === "string" ? role : role?.roleName || role?.RoleName
    )
  );
 
  // Values shown in the footer ("Updated by / Updated on")
  const [submittedUpdatedBy, setSubmittedUpdatedBy] = useState("");
  const [submittedUpdatedOn, setSubmittedUpdatedOn] = useState("");
 
  const [dateOfUpdate, setDateOfUpdate] = useState("");
  const [isReviewSectionChanged, setIsReviewSectionChanged] = useState(false);
  const [isReviewChecked, setIsReviewChecked] = useState((vesselCapacityChangeDetails && vesselCapacityChangeDetails[index]?.ReviewNeeded)?? false);
  const [isRemarksChanged, setIsRemarksChanged] = useState((vesselCapacityChangeDetails && vesselCapacityChangeDetails[index]?.Remarks )?? "");
 
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    getValues,
    clearErrors,
  } = useFormContext();
 
  const labelType = isGrossTonnage ? "Gross Tonnage" : "Deadweight";
  const fieldPrefix = `vesselCapacityChangeRequests.${index}`;
 
  // Load the saved record into the form. The audit fields are loaded as saved
  // (not overwritten with the current user) so they only change on a real edit.
 
useEffect(() => {
  if (!vesselCapacityChangeDetails?.length) return;
 
  const item = vesselCapacityChangeDetails.find(
    (x) => x.ChangeType === (isGrossTonnage ? 1 : 2)
  );
 
  if (!item) return;
 
  // Map backend values into React Hook Form
  const reviewNeeded = item.ReviewNeeded ?? false;
  const remarks = item.Remarks ?? "";
 
  setValue(
    `${fieldPrefix}.dateOfChange`,
    item.DateOfChange
      ? dayjs(item.DateOfChange).format("YYYY-MM-DD")
      : null
  );
 
  setValue(`${fieldPrefix}.oldValue`, roundOff(item.OldValue));
  setValue(`${fieldPrefix}.changeReason`, item.ChangeReason);
  setValue(`${fieldPrefix}.hasChanged`, item.HasChanged);
 
  // IMPORTANT: Use backend values, not local state
  setValue(`${fieldPrefix}.reviewNeeded`, reviewNeeded);
  setValue(`${fieldPrefix}.remarks`, remarks);
 
  // Keep local state synchronized with the backend
  setIsReviewChecked(reviewNeeded);
  setIsRemarksChanged(remarks);
 
  // Audit values
  setValue(`${fieldPrefix}.updatedBy`, item.UpdatedBy ?? "");
  setValue(`${fieldPrefix}.updatedOn`, item.UpdatedOn ?? null);
 
  setSubmittedUpdatedBy(item.UpdatedBy ?? "");
 
  setSubmittedUpdatedOn(
    item.UpdatedOn
      ? dayjs(item.UpdatedOn).format("DD-MMM-YYYY")
      : ""
  );
 
  setDateOfUpdate(
    item.DateOfUpdate
      ? dayjs(item.DateOfUpdate).format("DD-MMM-YYYY")
      : ""
  );
 
}, [
  vesselCapacityChangeDetails,
  index,
  isGrossTonnage,
  setValue,
  fieldPrefix
]);
 
  // Stamp who changed the review fields and when. Called only when the user
  // toggles "No new review is needed" or edits the remarks.
 
 
  const handleBlur = async (field, value) => {
    if (!isNaN(value)) {
      if (+value > 0) {
        setValue(field, roundOff(value));
      }
    } else {
      return null;
    }
    await trigger(field);
  };
 
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Top Input Details Container */}
      <div
        className="mt-2 d-flex flex-row ml-4 p-2"
        style={{ border: "2px solid #ccc" }}
      >
        <div style={{ flex: 1 }}>
          <MDBRow>
            {/* Date of Change Field */}
            <MDBCol md="12" className="mt-2">
              <Label required bold name="Date of Change" className="mb-1" />
              <Controller
                name={`${fieldPrefix}.dateOfChange`}
                control={control}
                defaultValue={null}
                rules={{
                  validate: (value) => {
                    if (!value) return "The date of change field is required";
                    return (
                      dayjs(value).isValid() || "Please enter a valid date"
                    );
                  },
                }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    disabled={isEnrollVessel}
                    onChange={(val) => {
                      field.onChange(
                        val && dayjs(val).isValid()
                          ? val.format("YYYY-MM-DD")
                          : null
                      );
                    }}
                    format="DD-MMM-YYYY"
                    localeText={{
                      fieldMonthPlaceholder: (params) =>
                        params.contentType === "letter" ? "MMM" : "MM",
                    }}
                    slotProps={{
                      actionBar: { actions: ["today"] },
                      textField: {
                        placeholder: "DD-MMM-YYYY",
                        sx: {
                          width: "100%",
                          "& .MuiInputBase-root": { height: 36 },
                          "& input": {
                            padding: "8px 10px",
                            fontSize: "14px",
                          },
                        },
                      },
                    }}
                  />
                )}
              />
              {errors.vesselCapacityChangeRequests?.[index]?.dateOfChange && (
                <div className="error-msg">
                  {
                    errors.vesselCapacityChangeRequests[index].dateOfChange
                      ?.message
                  }
                </div>
              )}
            </MDBCol>
 
            {/* Old Value Field */}
            <MDBCol md="12" className="mt-2">
              <Label required bold name={`Old ${labelType}`} className="mb-1" />
              <Controller
                name={`${fieldPrefix}.oldValue`}
                control={control}
                defaultValue=""
                rules={{
                  required: `The Old ${labelType} field is required`,
                  min: {
                    value: Number.EPSILON,
                    message: "Please enter a value greater than zero.",
                  },
                  validate: (value) => {
                    const currentValue = getValues(
                      isGrossTonnage
                        ? "vesselAttributes.grossTonnage"
                        : "vesselAttributes.deadweight"
                    );
 
                    return (
                      Number(value) !== Number(currentValue) ||
                      `Old ${labelType} and Current ${labelType} cannot be the same.\nPlease verify that the correct value has been entered.`
                    );
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`vessel_old_${
                      isGrossTonnage ? "gross_tonnage" : "deadweight"
                    }`}
                    autoFocus={false}
                    disabled={isEnrollVessel}
                    className="input-height"
                    type="number"
                    onBlur={() => handleBlur(field.name, field.value)}
                    onKeyDown={(e) => {
                      preventScientificNotation(e);
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onInput={(e) => {
                      if (e.target.value.length > 7) {
                        e.target.value = e.target.value.slice(0, 7);
                      }
                    }}
                    step={INPUT_STEP_VALUE}
                  />
                )}
              />
 
              <div
                className="mt-2"
                style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <strong>Date of Update:</strong>{dateOfUpdate}
              </div>
 
              {errors.vesselCapacityChangeRequests?.[index]?.oldValue && (
                <div className="error-msg" style={{ whiteSpace: "pre-line" }}>
                  {errors.vesselCapacityChangeRequests[index].oldValue?.message}
                </div>
              )}
            </MDBCol>
          </MDBRow>
        </div>
 
        {/* Change Reason Radio Inputs Container */}
        <div className="mt-2 ml-4" style={{ flex: 1 }}>
          <Controller
            name={`${fieldPrefix}.changeReason`}
            control={control}
            defaultValue={null}
            rules={{ required: "Please select a change reason" }}
            render={({ field }) => (
              <>
                <div
                  className="mt-2"
                  style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                >
                  <RadioInput
                    disabled={isEnrollVessel}
                    label="CII Reduction Measure"
                    checked={field.value === 1}
                    onChange={() => field.onChange(1)}
                    gap="true"
                  />
                </div>
 
                <div
                  className="mt-3"
                  style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                >
                  <RadioInput
                    disabled={isEnrollVessel}
                    label="Major Conversion"
                    checked={field.value === 2}
                    onChange={() => field.onChange(2)}
                    gap="true"
                  />
                </div>
 
                <div
                  className="mt-3"
                  style={{ fontSize: "0.75rem", fontWeight: "bold" }}
                >
                  <RadioInput
                    disabled={isEnrollVessel}
                    label="Neither a CII Reduction Measure nor a Major Conversion"
                    checked={field.value === 3}
                    onChange={() => field.onChange(3)}
                    gap="true"
                  />
                </div>
              </>
            )}
          />
          {errors.vesselCapacityChangeRequests?.[index]?.changeReason && (
            <div className="error-msg ml-4">
              {errors.vesselCapacityChangeRequests[index].changeReason?.message}
            </div>
          )}
        </div>
      </div>
 
      {/* Hidden audit fields. Registered outside the review box so they are
          always part of the form values and the payload. */}
      <Controller
        name={`${fieldPrefix}.updatedBy`}
        control={control}
        defaultValue=""
        render={() => null}
      />
      <Controller
        name={`${fieldPrefix}.updatedOn`}
        control={control}
        defaultValue={null}
        render={() => null}
      />
 
      {/* Review Box Section */}
      {showWarning && canViewReviewWarning && (
        <div
          className="mt-2 flex-row ml-4 p-2"
          style={{ border: "2px solid #ccc" }}
        >
          {/* Information Banner */}
          <div className="d-flex align-items-start">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "17px",
                height: "17px",
                borderRadius: "50%",
                backgroundColor: "#357ddc",
                color: "#fff",
                fontSize: "11px",
                fontWeight: "bold",
                marginRight: "5px",
                flexShrink: 0,
              }}
            >
              i
            </span>
 
            <div style={{ fontSize: "0.8rem" }}>
              <div>
                Date of Change occurred after verification of{" "}
                {reportYear ? `${reportYear} ` : ""}DCS Report.
              </div>
              <div>Please check if a new review is needed.</div>
            </div>
          </div>
 
          {/* No Review Needed Checkbox */}
          <div className="mt-2 ml-1">
            <Controller
              name={`${fieldPrefix}.reviewNeeded`}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <AbsCheckBox
                  slim={true}
                  id={`reviewNeeded_${index}`}
                  checked={Boolean(field.value)}
                 disabled={isEnrollVessel}
                  handleChange={(e) => {
                    if (isEnrollVessel) return;
                    const checked = e.target.checked;
                    field.onChange(checked);
                    setIsReviewChecked(checked);
 if (!checked) {
    clearErrors(`${fieldPrefix}.remarks`);
  }
                  }}
                  label={<span className="sm">No new review is needed</span>}
                />
              )}
            />
          </div>
 
          {/* Verification Input Box */}
          <div
            className="ml-4"
            style={{
              border: "1px solid #ccc",
              minHeight: "60px",
              padding: "10px",
              marginRight: "5px",
            }}
          >
            <div className="d-flex align-items-center">
              <Controller
                name={`${fieldPrefix}.isVerifiedAtChecked`}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <input
                    type="checkbox"
                   
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)
                     
                    }
                    disabled={isEnrollVessel}
                    style={{ marginRight: "6px" }}
                  />
                )}
              />
 
              <span
                style={{
                  fontSize: "0.8rem",
                  marginRight: "8px",
                  whiteSpace: "nowrap",
                }}
              >
               
              </span>
 
              <Controller
                name={`${fieldPrefix}.remarks`}
                control={control}
                defaultValue=""
                rules={{
    validate: (value) => {
      const isReviewNeededChecked = getValues(
        `${fieldPrefix}.reviewNeeded`
      );
 
      if (isReviewNeededChecked && !value?.trim()) {
        return "Remarks Required";
      }
 
      return true;
    },
  }}
                render={({ field }) => (
                  <input
                    type="text"
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setIsRemarksChanged(e.target.value);
                    }}
                    placeholder=""
                    disabled={isEnrollVessel}
                    style={{
                      border: "none",
                      outline: "none",
                      fontSize: "12px",
                      background: "transparent",
                      width: "100%",
                    }}
                  />
                )}
              />
             
            </div>
           
          </div>
          {errors.vesselCapacityChangeRequests?.[index]?.remarks && (
                <div className="error-msg ml-4">
                  {
                    errors.vesselCapacityChangeRequests[index].remarks
                      ?.message
                  }
                </div>
              )}
 
          {/* Footer Metadata */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              fontSize: "0.8rem",
              marginTop: "2px",
              marginRight: "5px",
              lineHeight: "15px",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto auto",
                columnGap: "6px",
                textAlign: "left",
              }}
            >
              <span>Updated by:</span>
              <span>{submittedUpdatedBy}</span>
 
              <span>Updated on:</span>
              <span>{submittedUpdatedOn}</span>
            </div>
          </div>
        </div>
      )}
    </LocalizationProvider>
  );
};
 
export default DWTGTChangeDetails;
 